import { useMemo } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Settings2, RotateCcw, Columns2, Square } from "lucide-react";
import { FeatureGate } from "@/components/FeatureGate";
import {
  Popover,
  PopoverTrigger,
  PopoverPopup,
} from "@/components/ui/Popover";
import { Switch } from "@/components/ui/Switch";
import { cn } from "@/utils/Functions";
import { useDashboardLayout } from "./useDashboardLayout";
import type { WidgetDefinition, WidgetLayoutItem } from "./constants";

function SortableWidgetWrapper({
  layout,
  definition,
}: {
  layout: WidgetLayoutItem;
  definition: WidgetDefinition;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: layout.id });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  const Component = definition.component;
  const Fallback = definition.featureFallback;

  const dragHandleProps = {
    attributes: attributes as unknown as Record<string, unknown>,
    listeners: listeners as Record<string, unknown> | undefined,
  };

  const content = definition.featureFlag ? (
    <FeatureGate
      flag={definition.featureFlag}
      fallback={Fallback ? <Fallback /> : null}
      loadingFallback={Fallback ? <Fallback /> : null}
    >
      <Component dragHandleProps={dragHandleProps} />
    </FeatureGate>
  ) : (
    <Component dragHandleProps={dragHandleProps} />
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        layout.size === 2 && "md:col-span-2",
        isDragging && "opacity-50 z-10",
      )}
    >
      {content}
    </div>
  );
}

const DashboardPage = () => {
  const {
    loaded,
    visibleWidgets,
    allWidgets,
    handleDragEnd,
    toggleWidget,
    setWidgetSize,
    resetLayout,
  } = useDashboardLayout();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor),
  );

  const sortableIds = useMemo(
    () => visibleWidgets.map((w) => w.layout.id),
    [visibleWidgets],
  );

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    handleDragEnd(String(active.id), String(over.id));
  };

  if (!loaded) return null;

  return (
    <div className="min-h-full">
      <header className="p-3 border-b border-border sticky top-0 bg-background z-10 flex items-center justify-between">
        <div>
          <h1 className="font-semibold">Dashboard</h1>
          <p className="text-muted-foreground text-sm">
            Your overview at a glance
          </p>
        </div>

        <Popover>
          <PopoverTrigger className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
            <Settings2 className="size-4" />
            Customize
          </PopoverTrigger>
          <PopoverPopup
            side="bottom"
            align="end"
            sideOffset={8}
            className="w-72 rounded-xl border border-border bg-popover shadow-lg"
          >
            <div className="flex items-center justify-between pb-3 border-b border-border">
              <span className="text-sm font-semibold">Widgets</span>
              <button
                type="button"
                onClick={resetLayout}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <RotateCcw className="size-3" />
                Reset
              </button>
            </div>

            <div className="divide-y divide-border">
              {allWidgets.map(({ layout, definition }) => (
                <div
                  key={layout.id}
                  className="flex items-center justify-between gap-3 py-2.5"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <Switch
                      checked={layout.enabled}
                      onCheckedChange={() => toggleWidget(layout.id)}
                    />
                    <span
                      className={cn(
                        "text-sm truncate",
                        !layout.enabled && "text-muted-foreground",
                      )}
                    >
                      {definition.label}
                    </span>
                  </div>

                  {layout.enabled && (
                    <div className="flex items-center gap-0.5 shrink-0">
                      <button
                        type="button"
                        onClick={() => setWidgetSize(layout.id, 1)}
                        className={cn(
                          "rounded p-1 transition-colors",
                          layout.size === 1
                            ? "bg-muted text-foreground"
                            : "text-muted-foreground hover:text-foreground",
                        )}
                        title="1 column"
                      >
                        <Square className="size-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setWidgetSize(layout.id, 2)}
                        className={cn(
                          "rounded p-1 transition-colors",
                          layout.size === 2
                            ? "bg-muted text-foreground"
                            : "text-muted-foreground hover:text-foreground",
                        )}
                        title="2 columns"
                      >
                        <Columns2 className="size-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </PopoverPopup>
        </Popover>
      </header>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onDragEnd}
      >
        <SortableContext items={sortableIds} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 p-4 overflow-x-clip">
            {visibleWidgets.map(({ layout, definition }) => (
              <SortableWidgetWrapper
                key={layout.id}
                layout={layout}
                definition={definition}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default DashboardPage;
